import React, { useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import {
  VOID_REASON_LABELS, VOID_REASON_DESCRIPTIONS, checkVoidAllowed, describeVoidEffect,
  type VoidReasonCode, type VoidTarget, type VoidPolicy,
} from '../utils/slipVoid';

/**
 * 伝票の取消モーダル。
 *
 * 押す前に「何が起きるか」を全部見せることが目的。
 * 伝票番号がどうなるか、売上とバックがどう動くか、記録は残るのか。
 * 現場が一番知りたいのはそこで、それが分からないから
 * 「消していいのか分からない」まま操作されることになる。
 */

export interface VoidSlipDialogProps {
  open: boolean;
  onClose: () => void;
  target: VoidTarget | null;
  userRole: string;
  policy?: Partial<VoidPolicy>;
  onConfirm: (input: { reasonCode: VoidReasonCode; note: string; reissue: boolean }) => Promise<void>;
}

const VoidSlipDialog: React.FC<VoidSlipDialogProps> = ({
  open, onClose, target, userRole, policy, onConfirm,
}) => {
  const [reasonCode, setReasonCode] = useState<VoidReasonCode>('input_error');
  const [note, setNote] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const check = useMemo(
    () => (target ? checkVoidAllowed(target, { userRole, policy }) : null),
    [target, userRole, policy]
  );
  const effect = useMemo(
    () => (target ? describeVoidEffect(target, reasonCode) : null),
    [target, reasonCode]
  );

  const noteRequired = reasonCode === 'other' || (policy?.requireNote ?? true);
  const canSubmit =
    !!check?.allowed && acknowledged && (!noteRequired || note.trim().length > 0) && !submitting;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onConfirm({ reasonCode, note: note.trim(), reissue: reasonCode === 'reissue' });
      setNote('');
      setAcknowledged(false);
      setReasonCode('input_error');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (!target) return null;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        伝票を取り消す
        <Typography variant="body2" color="text.secondary">
          {target.slipNumber ?? '（番号なし）'} / ¥{target.total.toLocaleString()}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {check && check.blockers.length > 0 && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            <AlertTitle>この伝票は取り消せません</AlertTitle>
            {check.blockers.join('\n\n')}
          </Alert>
        )}

        {check?.allowed && (
          <>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              取消の理由
            </Typography>
            <TextField
              select fullWidth size="small" value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value as VoidReasonCode)}
              sx={{ mb: 1 }}
            >
              {(Object.keys(VOID_REASON_LABELS) as VoidReasonCode[]).map((k) => (
                <MenuItem key={k} value={k}>{VOID_REASON_LABELS[k]}</MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {VOID_REASON_DESCRIPTIONS[reasonCode]}
            </Typography>

            <TextField
              fullWidth multiline rows={2} size="small"
              label={noteRequired ? '理由の記入（必須）' : '理由の記入'}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例: ボトル1本を二重に打ってしまったため"
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              取り消すと、こうなります
            </Typography>
            <Stack spacing={1.2} sx={{ mb: 2 }}>
              {effect && ([
                ['伝票番号', effect.slipNumberAfter],
                ['売上', effect.salesEffect],
                ['バック', effect.backEffect],
                ['領収書', effect.receiptEffect],
                ['記録', effect.recordEffect],
              ] as [string, string][]).map(([label, text]) => (
                <Box key={label} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Chip size="small" label={label} sx={{ minWidth: 72, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {text}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {check.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                {check.warnings.join('\n\n')}
              </Alert>
            )}

            {check.needsApproval && (
              <Alert severity="info" sx={{ mb: 2 }}>
                この操作は責任者の承認待ちとして記録されます。承認されるまで売上は変わりません。
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  上の内容を確認しました。取消の記録が残ることを理解しています。
                </Typography>
              }
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>やめる</Button>
        <Button
          variant="contained" color="error"
          onClick={handleConfirm} disabled={!canSubmit}
        >
          {reasonCode === 'reissue' ? '取り消して打ち直す' : '取り消す'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoidSlipDialog;
